import BusinessReview from "@/components/VenueDetailSheet/components/BusinessReview";
import { useDarkMode } from "@/hooks/useDarkMode";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shadcn/components/ui/dropdown-menu";

import { useRouter } from "@tanstack/react-router";
import { createFileRoute } from "@tanstack/react-router";
import { useI18N } from "common/hooks";
import { useSyncReviews } from "common/hooks/business/details/useSyncReviews";
import { useEffect, useState } from "react";
import NoReviews from "@/assets/images/noreviews.svg";
import Loader from "@/components/common/Loader";

type VenueReviewsRouteSearchParams = {
  sortBy: "popularity" | "latest";
};

export const Route = createFileRoute("/venuereviews/$venueId")({
  component: VenueReviews,
  validateSearch: (
    search: Record<string, unknown>
  ): VenueReviewsRouteSearchParams => {
    return {
      sortBy: search.source as VenueReviewsRouteSearchParams["sortBy"],
    };
  },
});

function VenueReviews() {
  const { venueId } = Route.useParams();
  const { sortBy } = Route.useSearch();
  const isDark = useDarkMode();
  const i18n = useI18N();

  const [currentSort, setCurrentSort] = useState<"popularity" | "latest">(
    sortBy || "latest"
  );

  const { data, error, isLoading, loadReviews } = useSyncReviews({
    venueId,
    sortBy: currentSort,
  });

  const router = useRouter();
  const onBack = () => router.history.back();

  useEffect(() => {
    if (error) {
      console.error(error);
    }
  }, [error]);

  useEffect(() => {
    loadReviews();
  }, [currentSort]);

  return (
    <div className="px-4">
      <main className="max-w-[1160px] mx-auto">
        <div className="flex items-center bg-white dark:bg-primary-900 mb-2 mt-1 justify-between">
          <button
            onClick={() => onBack()}
            className="flex items-center gap-2 px-1 py-3 bg-white dark:bg-primary-900 rounded-3xl text-center button-small-semi-bold"
          >
            <div className="w-6 h-6 relative">
              <img
                src={
                  isDark
                    ? "/images/ArrowLeft_dark.svg"
                    : "/images/ArrowLeft.svg"
                }
                alt=""
              />
            </div>
            {i18n.get("back")}
          </button>
        </div>
        <section className="flex justify-between items-center mb-4">
          <h1 className="text-primary-800 dark:text-primary-150 desktop-h4-medium-semi-bold">
            {i18n.get("allReviews")}
          </h1>

          <DropdownMenu>
            <DropdownMenuTrigger>
              <div className="h-10 px-4 bg-white dark:bg-primary-900 rounded-3xl border border-blue-950 flex items-center gap-1 w-full max-w-[120px] md:max-w-[100px] justify-center">
                <div className="w-6 h-6 relative">
                  <img
                    src={
                      isDark
                        ? "/images/ListBullets_dark.svg"
                        : "/images/ListBullets.svg"
                    }
                    alt=""
                  />
                </div>
                <span className="button-small-semi-bold text-primary-800 dark:text-primary-150">
                  Sort
                </span>
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="dark:bg-primary-800 bg-white"
              align="end"
            >
              <DropdownMenuLabel>Sort By</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuRadioGroup
                value={currentSort}
                onValueChange={(value) => {
                  setCurrentSort(value as any);
                }}
              >
                <DropdownMenuRadioItem value="latest" className="hover:bg-gray-200 focus:bg-gray-200 dark:hover:bg-primary-700 dark:focus:bg-primary-700 rounded-md mx-2 my-1">
                  Latest
                </DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="popularity" className="hover:bg-gray-200 focus:bg-gray-200 dark:hover:bg-primary-700 dark:focus:bg-primary-700 rounded-md mx-2 my-1">
                  Popularity
                </DropdownMenuRadioItem>
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </section>
        <div className="grid grid-col-1 md:grid-cols-3 gap-4">
          {data?.map((review) => (
            <div key={review.id}>
              <BusinessReview review={review} onFlag={() => {}} />
            </div>
          ))}
        </div>
        {data?.length === 0 && !isLoading && (
          <img
            src={NoReviews}
            alt="No reviews"
            className="max-w-[70%] mx-auto mt-20"
          />
        )}
        {isLoading && <Loader />}
      </main>
    </div>
  );
}

export default VenueReviews;
